/**
 * Pure star-force benchmark comparison logic. `benchmark_template.slots` (WP3 seed) is
 * account-wide, not a real per-slot breakdown ("doc gives an account-wide benchmark... no per-slot
 * values are fabricated" — see `starforce.json`): a single `uniformTargetStar` (or a
 * `uniformTargetStarRange`) applies to every gear slot alike. This module applies that one target
 * to each of the frozen 24 slots and reports met/unmet — it does not invent per-slot targets the
 * seed data doesn't have.
 */
import type { BenchmarkTemplateDto } from './reference';

/** The benchmark's primary target star — the range's lower bound when it's a range. */
export function primaryTargetStar(template: BenchmarkTemplateDto): number | null {
  if (typeof template.slots.uniformTargetStar === 'number') {
    return template.slots.uniformTargetStar;
  }
  if (template.slots.uniformTargetStarRange) {
    return template.slots.uniformTargetStarRange[0];
  }
  return null;
}

/** The benchmark's stretch target star, if the template names one (e.g. a push range's top end). */
export function stretchTargetStar(template: BenchmarkTemplateDto): number | null {
  if (template.slots.stretchTargetStarRange) {
    return template.slots.stretchTargetStarRange[1];
  }
  if (template.slots.uniformTargetStarRange) {
    return template.slots.uniformTargetStarRange[1];
  }
  return null;
}

export interface SlotComparison {
  slotKey: string;
  stars: number;
  target: number;
  met: boolean;
}

/** Compares each slot's actual stars against the benchmark's single uniform target. */
export function compareSlotsToBenchmark(
  slots: readonly { slotKey: string; stars: number }[],
  template: BenchmarkTemplateDto,
): SlotComparison[] {
  const target = primaryTargetStar(template);
  return slots.map((slot) => ({
    slotKey: slot.slotKey,
    stars: slot.stars,
    target: target ?? 0,
    met: target !== null && slot.stars >= target,
  }));
}

/** {@code "n/m slots at target"} summary. */
export function benchmarkSummary(comparisons: readonly SlotComparison[]): { met: number; total: number } {
  return { met: comparisons.filter((c) => c.met).length, total: comparisons.length };
}
